import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardContent, LinearProgress, Alert, Chip, Divider, AppBar, Toolbar } from '@mui/material';
import MenuDrawer from "../components/navbar";
import supabase from '../services/supabaseClient';
import Logo from "frontend/public/images/logo.png";

interface BuddyWarrior {
  buddy_id: number;
  warrior_uuid: string;
  warrior_username: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
  timestamp: string;
}

interface WarriorAddiction {
  addict_id: number;
  uuid: string;
  addict_type: string;
  days_clean: number;
  goal_weeks: number;
  username: string;
  is_verified: boolean;  // Add this field to track verification status
}

const ViewBuddyWarriors = () => {
  const [buddyWarriors, setBuddyWarriors] = useState<BuddyWarrior[]>([]);
  const [warriorAddictions, setWarriorAddictions] = useState<{ [key: string]: WarriorAddiction[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBuddyWarriors = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current user
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) throw new Error('Not authenticated');

        // Fetch all warriors where the current user is a buddy
        const { data: buddies, error: buddyError } = await supabase
          .from('buddies')
          .select('*')
          .eq('buddy_uuid', user.id)
          .order('timestamp', { ascending: false });

        if (buddyError) throw buddyError;

        if (!buddies) {
          setBuddyWarriors([]);
          return;
        }

        setBuddyWarriors(buddies);

        // Fetch addiction data for accepted warriors
        const addictionData: { [key: string]: WarriorAddiction[] } = {};
        
        for (const buddy of buddies.filter(b => b.status === 'accepted')) {
          const { data: addictions, error: addictError } = await supabase
            .from('warriors')
            .select('*')
            .eq('uuid', buddy.warrior_uuid);

          if (addictError) throw addictError;
          
          if (addictions) {
            // Initialize is_verified field for each addiction
            addictionData[buddy.warrior_uuid] = addictions.map((addiction) => ({
              ...addiction,
              is_verified: false, // Initially set is_verified to false
            }));
          }
        }

        setWarriorAddictions(addictionData);
      } catch (err) {
        console.error('Error fetching buddy warriors:', err);
        setError('Failed to load buddy warriors');
      } finally {
        setLoading(false);
      }
    };

    fetchBuddyWarriors();
  }, []);

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LinearProgress sx={{ width: '80%', maxWidth: '400px' }} />
      </Box>
    );
  }

  const pendingRequests = buddyWarriors.filter(w => w.status === 'pending');
  const acceptedWarriors = buddyWarriors.filter(w => w.status === 'accepted');
  const otherRequests = buddyWarriors.filter(w => ['rejected', 'cancelled'].includes(w.status));

  const handleReport = async (uuid: string, addictType: string) => {
    try {
      setLoading(true);
  
      const { error } = await supabase
        .from('warriors')
        .update({ days_clean: 0 })
        .eq('uuid', uuid)
        .eq('addict_type', addictType);
  
      if (error) throw error;
  
      setWarriorAddictions((prevState) => {
        const updatedAddictions = { ...prevState };
        updatedAddictions[uuid] = updatedAddictions[uuid].map((addiction) =>
          addiction.uuid === uuid && addiction.addict_type === addictType
            ? { ...addiction, days_clean: 0 }
            : addiction
        );
        return updatedAddictions;
      });
  
      setError(null); 
    } catch (err) {
      console.error('Error reporting warrior:', err);
      setError('Failed to report the warrior');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = (uuid: string, addictType: string) => {
    setWarriorAddictions((prevState) => {
      const updatedAddictions = { ...prevState };
      updatedAddictions[uuid] = updatedAddictions[uuid].map((addiction) =>
        addiction.uuid === uuid && addiction.addict_type === addictType
          ? { ...addiction, is_verified: true } // Mark addiction as verified
          : addiction
      );
      return updatedAddictions;
    });
  };

  return (
    <>
      <AppBar position="static" color="transparent" elevation={1} sx={{ px: 0 }}>
        <Toolbar sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <MenuDrawer />
            <img src={Logo} alt="BreakFree Logo" style={{ height: 40, marginLeft: 10 }} />
            <Typography variant="h6" sx={{ fontWeight: "bold", ml: 1, color: "black" }}>
              BreakFree
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 10, p: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom textAlign="center" sx={{ mb: 4 }}>
          My Warriors
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {buddyWarriors.length === 0 ? (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="body1" textAlign="center">
                You are not currently supporting any warriors. 
                Find a warrior to start supporting their journey!
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Pending Requests Section */}
            {pendingRequests.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Pending Support Requests
                  </Typography>
                  {pendingRequests.map((warrior) => (
                    <Box key={warrior.buddy_id} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{warrior.warrior_username}</Typography>
                        <Chip 
                          label="Pending"
                          size="small"
                          color="warning"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Requested: {new Date(warrior.timestamp).toLocaleDateString()}
                      </Typography>
                      <Divider sx={{ mt: 1 }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Active Warriors Section */}
            {acceptedWarriors.map((warrior) => (
              <Card key={warrior.buddy_id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h6">
                      {warrior.warrior_username}'s Journey
                    </Typography>
                    <Chip 
                      label="Supporting"
                      size="small"
                      color="success"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Supporting since: {new Date(warrior.timestamp).toLocaleDateString()}
                  </Typography>

                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                    Current Battles:
                  </Typography>
                  
                  {warriorAddictions[warrior.warrior_uuid]?.length > 0 ? (
                    warriorAddictions[warrior.warrior_uuid].map((addiction) => {
                      const weeksClean = Math.floor(addiction.days_clean / 7);
                      const progress = Math.min((weeksClean / addiction.goal_weeks) * 100, 100);
                      
                      return (
                        <Box key={addiction.addict_id} sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            • {addiction.addict_type.charAt(0).toUpperCase() + addiction.addict_type.slice(1)}
                            {addiction.is_verified && ' ✅'} {/* Display verified tick */}
                          </Typography>
                          
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ mt: 1, mb: 1, height: 8, borderRadius: 4 }}
                          />

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              {addiction.days_clean} days clean (Goal: {addiction.goal_weeks * 7} days)
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button variant="outlined" color="error" size="small" onClick={() => handleReport(addiction.uuid, addiction.addict_type)}>
                                Report
                              </Button>
                              {!addiction.is_verified && (
                              <Button variant="contained" color="primary" onClick={() => handleVerify(warrior.warrior_uuid, addiction.addict_type)}>
                                Verify
                              </Button>
                            )}
                            </Box>                            
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No addictions data available.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </Box>
    </>
  );
};

export default ViewBuddyWarriors;